import { getUnlockString } from '@/scripts/utils/activity'
import { toSlug } from '@/scripts/utils/string'
import { getIframeBaseUrl } from '@/scripts/utils/iframe'
import { isMobileOrTablet, isAndroid, isIOS } from '@/scripts/helpers/features'
import { nowDayNumber } from '@/scripts/config/settings'
import { GlobalState } from '@/scripts/config/globalState'
import type { Activity, RawActivity } from './types'

let activities: Activity[] = []

function getVideoUrl(idecOrBonus: string | null): string {
  if (!idecOrBonus) return ''

  const value = String(idecOrBonus)

  let paramName = 'IDEC'
  let id = value
  if (value.includes('BONUS_')) {
    id = value.split('_')[1]
    paramName = 'bonus'
  }

  // Only use DP if it's available and initialized
  // If DP isn't ready, return empty string (URL will be empty but won't cause errors)
  try {
    const DP = window.DP as { createIframePlayerUrl?: (params: Record<string, string | boolean>) => string } | undefined
    return DP?.createIframePlayerUrl ? DP.createIframePlayerUrl({ [paramName]: id, autoStart: true }) : ''
  } catch (error) {
    console.warn('[getVideoUrl] DP not ready, returning empty URL:', error)
    return ''
  }
}

export function processActivities(rawActivities: RawActivity[], callback?: () => void): void {
  let callbackCalled = false

  const run = () => {
    // Prevent multiple calls
    if (callbackCalled) return

    try {
      const processedActivities = rawActivities.map((rawActivity) => {
        let appLink: string | undefined = undefined

        if (isAndroid && rawActivity.appLinksPlay) {
          appLink = rawActivity.appLinksPlay
        } else if (isIOS && rawActivity.appLinksIos) {
          appLink = rawActivity.appLinksIos
        }

        const isActivityOpen = !!rawActivity.id && rawActivity.day <= nowDayNumber
        const isGame = !!rawActivity.uuid
        const hasAlt =
          isActivityOpen &&
          !appLink &&
          isMobileOrTablet &&
          !!rawActivity.altTitle &&
          !!rawActivity.altDescription &&
          !!rawActivity.altIdec

        const isExternalPage = !rawActivity.uuid && !rawActivity.idec
        const description = hasAlt && !isExternalPage ? rawActivity.altDescription : rawActivity.description
        const title = hasAlt && !isExternalPage ? rawActivity.altTitle! : rawActivity.title
        // Always preserve the original linkHash for URL routing, even when using alt content
        // This ensures hash-based navigation works correctly on mobile
        const linkHash = rawActivity.linkHash || toSlug(rawActivity.title)
        const label = rawActivity.idec || (hasAlt && !isExternalPage) ? 'Přehrát' : rawActivity.label

        const unlockString = !isActivityOpen ? getUnlockString(rawActivity.day, nowDayNumber) : undefined
        // URL logic:
        // - Desktop: Always use game iframe if uuid exists (hasAlt is false on desktop)
        // - Mobile: If uuid exists but no app links (hasAlt is true), use alt video, otherwise use game iframe
        const url = hasAlt
          ? getVideoUrl(rawActivity.altIdec)
          : isGame
            ? `${getIframeBaseUrl()}/flashAppIframe/${rawActivity.uuid}?posterEnabled=false&closeButtonEnabled=false`
            : getVideoUrl(rawActivity.idec ?? '')

        return {
          positionId: rawActivity.positionId,
          title,
          description: description || '',
          day: rawActivity.day || 0,
          isActivityOpen,
          label,
          url,
          linkHash,
          appLink,
          unlockString,
          isExternalPage,
          hasAlt,
          gameType: rawActivity.gameType,
        } satisfies Activity
      })

      activities = processedActivities
      setGlobalState()
      callbackCalled = true
      if (callback) callback()
    } catch (error) {
      console.error('[processActivities] Error in run():', error)
      // Still call callback even on error
      callbackCalled = true
      if (callback) callback()
    }
  }

  // Always wait for DP.ready() if it exists, but use a timeout fallback
  // On Android, DP.ready() might not fire, so the timeout ensures we proceed anyway
  if (window.DP && typeof window.DP.ready === 'function') {
    try {
      // Use a longer timeout on Android since DP might take longer to initialize
      const timeoutMs = isAndroid ? 3000 : 1000
      const timeoutId = setTimeout(() => {
        if (!callbackCalled) {
          console.warn(`[processActivities] DP.ready timeout after ${timeoutMs}ms, running anyway`)
          run()
        }
      }, timeoutMs)

      window.DP.ready(() => {
        clearTimeout(timeoutId)
        run()
      })
    } catch (error) {
      console.error('[processActivities] Error with DP.ready:', error)
      // If DP.ready fails, run anyway
      run()
    }
  } else {
    // No DP.ready function - run immediately
    run()
  }
}

export function setGlobalState(): void {
  GlobalState.activities = activities
}
